/**
 * The seam between the chat UI and the ratchet (ADR-016).
 *
 * One place that knows how a message becomes ciphertext and back, so the
 * screens keep dealing in messages. Everything it needs is injected, so it
 * runs under node --test.
 *
 * The envelope is ADR-011 §3.1: the type, the text, the file metadata and the
 * quoted preview all travel INSIDE, because the server can no longer supply
 * any of them — it used to attach the file name and the quote from its own
 * copy, and that copy is what is going away.
 */
import { pad, unpad, PAD_BLOCK } from './convKey.mjs';

export const ENVELOPE_VERSION = 1;
export { PAD_BLOCK };

const b64 = (u8) => btoa(String.fromCharCode(...u8));
export function unb64(s) {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** What gets sealed. Nothing here is visible to the server. */
export function buildEnvelope({ clientMid, msgType, content, fileMeta = null, reply = null, ts = null, gossip = null }) {
  const e = {
    v: ENVELOPE_VERSION,
    mid: clientMid,
    t: msgType || 'text',
    text: content || '',
    ts: ts ?? Date.now(),
  };
  if (fileMeta) e.file = fileMeta;
  if (reply) e.reply = reply;
  // The sender's view of the key-log head, sealed where the server can
  // neither read it nor strip it. Every conversation audits the log
  // (keyLogGossip.mjs); an added JSON field costs old readers nothing.
  if (gossip) e.kl = gossip;
  // Padded to 256-byte blocks (ADR-011 §3.1). Without it the ciphertext is as
  // long as the message: "ок" and "хорошо" and "нет" are all distinguishable
  // by size to anyone watching, and so is a one-word reply from a paragraph.
  // The ratchet encrypts what it is given and pads nothing itself, so the
  // padding has to happen here or nowhere — and "nowhere" is invisible until
  // somebody measures a row in the database.
  return pad(new TextEncoder().encode(JSON.stringify(e)));
}

/**
 * Read an envelope in either form.
 *
 * Padding was added after devices were already talking, and devices update
 * independently — so for a while one end sends padded and the other expects
 * unpadded. The reader that only knew the new form answered "Сообщение
 * повреждено" to every message from a phone that had not updated yet, and to
 * the sender everything looked fine.
 *
 * The lesson is the general one: a change to the wire format has to be
 * recognisable by the reader, or skew turns into lost messages. Here it is
 * recognisable after the fact — a padded envelope starts with a two-byte
 * length, an unpadded one starts with '{' — so both are accepted and the
 * transition costs nothing. A future change gets a version byte instead of
 * this.
 */
export function readEnvelope(bytes) {
  const decoder = new TextDecoder();
  const tryParse = (u8) => {
    if (!u8 || !u8.length) return null;
    try {
      const parsed = JSON.parse(decoder.decode(u8));
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) { return null; }
  };

  const parsed = tryParse(unpad(bytes)) || tryParse(bytes);
  if (!parsed) throw new Error('bad padding');
  if (parsed.v !== ENVELOPE_VERSION) throw new Error('unknown envelope version');
  return parsed;
}

/**
 * Turn one message into the frames that carry it — one per recipient device,
 * plus this account's own other devices.
 *
 * Returns what to send AND what could not be sent. "Delivered to 2 of 3
 * devices" is a fact the screen may want; silently dropping the third is how
 * somebody's laptop stops receiving without anybody noticing.
 */
export async function encryptMessage(sessions, {
  toUserId, clientMid, msgType, content, fileMeta, reply, verifyBundle, outerMid, gossip,
}) {
  // The envelope keeps the ORIGINAL id of the message while the row that
  // carries it gets a new one. That is what makes a resend possible at all:
  // the server collapses a repeat onto the existing row by client_mid, so a
  // resend under the same id would be acknowledged and never delivered — and
  // the receiver still needs to recognise it as the message it asked for.
  const payload = buildEnvelope({ clientMid, msgType, content, fileMeta, reply, gossip });
  const rowMid = outerMid || clientMid;
  const results = await sessions.encryptFor(toUserId, payload, { verifyBundle });
  const send = [];
  const failed = [];
  for (const r of results) {
    if (r.error || !r.body) { failed.push({ deviceId: r.deviceId, userId: r.userId, error: r.error }); continue; }
    send.push({
      type: 'chat_message',
      // The conversation, not the key holder. Every copy of one message —
      // the peer's devices and our own — belongs to the same thread, and
      // target_device_id already says who can open it. Addressing our own
      // copy to ourselves put it in a conversation with ourselves, where the
      // thread it belongs to would never show it; the server, meanwhile,
      // refused it outright, because a user is not their own friend.
      to: toUserId,
      encV: 1,
      msgType: 'enc',
      content: b64(r.body),
      targetDeviceId: r.deviceId,
      senderDeviceId: sessions.deviceId,
      clientMid: rowMid,
    });
  }
  // A device we refused to trust blocks the whole message, not just its own
  // copy. Sending to the two devices we do trust and quietly skipping the
  // third is worse than not sending: the conversation looks normal while an
  // unverified key sits in it. This is the behaviour Signal has when a safety
  // number changes, and it is deliberate friction.
  const blocked = failed.filter(f => f.error === 'untrusted');
  if (blocked.length) return { send: [], failed, blocked };
  return { send, failed, blocked: [] };
}

/**
 * Open one stored row, or say why not.
 *
 * A message that cannot be opened is shown as such rather than dropped: a
 * conversation with a hole in it and no explanation is worse than one that
 * admits a message did not decrypt.
 */
/**
 * Build the row the screen shows from the bytes the ratchet handed back.
 *
 * Separate from decryption because the intake pipeline writes the result down
 * before it lets the ratchet move, and it needs the finished row to write.
 */
export function rowFromPlaintext(row, plaintext) {
  const e = readEnvelope(plaintext);
  return {
    ...row,
    content: e.text,
    msgType: e.t,
    type: e.t,
    client_mid: e.mid || row.client_mid,
    envelopeTs: e.ts,
    // The envelope outranks the server's files table: since SEC-04 the
    // server holds a blob named blob.bin, and the real name, MIME, size and
    // the KEY that opens it exist only in here. `file_key` absent means the
    // pre-encryption era — the bytes come back plain and need no key.
    ...(e.file ? {
      file_id: e.file.id ?? row.file_id ?? null,
      file_key: e.file.k || null,
      file_name: e.file.name, file_mime: e.file.mime, file_size: e.file.size, file_duration: e.file.dur,
    } : {}),
    ...(e.reply ? { reply_to_id: e.reply.id, reply_content: e.reply.preview, reply_msg_type: e.reply.t, reply_from_id: e.reply.fromId } : {}),
    ...(e.kl ? { key_log_gossip: e.kl } : {}),
  };
}

/**
 * Open one stored row and commit the ratchet in one step.
 *
 * The app does NOT go through here: opening is spending, and deciding when to
 * spend belongs to the intake pipeline (chatIntake.mjs), which writes the
 * plaintext down before the session is allowed to move. This is for callers
 * that hold no archive — tests, and reading an exported history back.
 */
export async function decryptRow(sessions, row) {
  if (!row || row.enc_v !== 1) return row;                     // plaintext era
  if (!row.sender_device_id) return { ...row, decryptError: 'E_NO_SENDER' };
  try {
    const out = await sessions.decryptFrom(row.from_user_id, row.sender_device_id, unb64(row.content));
    if (!out) return { ...row, decryptError: 'E_BAD_MAC' };
    return rowFromPlaintext(row, out);
  } catch (err) {
    return { ...row, decryptError: 'E_ENVELOPE' };
  }
}

export const DECRYPT_ERROR_TEXT = {
  E_NO_SENDER: 'Сообщение без отправителя — расшифровать нечем',
  E_BAD_MAC: 'Не удалось расшифровать (сессия сброшена или сообщение не для этого устройства)',
  E_ENVELOPE: 'Сообщение повреждено',
  E_LEGACY_AFTER_CUTOFF: 'Незашифрованное сообщение после перехода на шифрование — не показывается',
};
